import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { TipoTributarioStruct } from '@shared/services/structs/motor-fiscal/tipo-tributario.struct';
import { formatarCadastrosVinculados } from '@shared/helpers/vinculo-tipo-tributario.helper';

export interface ResolverVinculoModalData {
  /** Exclusão em massa (vários tipos selecionados) ou individual. */
  isBulk: boolean;
  /** "00 — Tributada integralmente"; usado apenas na exclusão individual. */
  descricaoAlvo: string;
  /** Cadastros de pessoa vinculados aos tipos que serão excluídos. */
  totalPessoas: number;
  /** Vendedores vinculados aos tipos que serão excluídos (vendedor tem id_tipo_tributario próprio). */
  totalVendedores: number;
  /** Tipos ativos que podem receber a reatribuição (já excluídos os que serão removidos). */
  candidatos: TipoTributarioStruct[];
}

export interface ResolverVinculoModalResult {
  confirm: boolean;
  /** null quando o usuário optou por deixar os cadastros sem tipo tributário. */
  idTipoTributarioDestino: number | null;
}

export type ModoResolucao = 'reatribuir' | 'desvincular';

@Component({
  selector: 'app-resolver-vinculo-modal',
  standalone: true,
  imports: [
    CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule,
    MatRadioModule, MatFormFieldModule, MatSelectModule, AlertCardComponent,
  ],
  templateUrl: './resolver-vinculo-modal.component.html',
  styleUrl: './resolver-vinculo-modal.component.css',
})
export class ResolverVinculoModalComponent {
  public modo: ModoResolucao;
  public idDestino: number | null = null;

  constructor(
    public dialogRef: MatDialogRef<ResolverVinculoModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ResolverVinculoModalData
  ) {
    // Sem candidatos não há o que escolher: resta desvincular (RN10).
    this.modo = this.temCandidatos ? 'reatribuir' : 'desvincular';
  }

  public get temCandidatos(): boolean {
    return this.data.candidatos.length > 0;
  }

  public get titulo(): string {
    return this.data.isBulk
      ? 'Tipos tributários vinculados a cadastros'
      : 'Tipo tributário vinculado a cadastros';
  }

  public get mensagem(): string {
    const vinculos = formatarCadastrosVinculados(this.data.totalPessoas, this.data.totalVendedores);
    return this.data.isBulk
      ? `Os tipos tributários selecionados estão vinculados a ${vinculos}. Antes de excluir, defina o que fazer com esse(s) cadastro(s).`
      : `O tipo tributário "${this.data.descricaoAlvo}" está vinculado a ${vinculos}. Antes de excluir, defina o que fazer com esse(s) cadastro(s).`;
  }

  public get mensagemSemCandidatos(): string {
    const vinculos = formatarCadastrosVinculados(this.data.totalPessoas, this.data.totalVendedores);
    return `Não há outro tipo tributário ativo disponível para reatribuição. Ao excluir, ${vinculos} ficarão sem tipo tributário.`;
  }

  public get isConfirmDisabled(): boolean {
    return this.modo === 'reatribuir' && this.idDestino == null;
  }

  public cancelar(): void {
    this.dialogRef.close();
  }

  public confirmar(): void {
    if (this.isConfirmDisabled) return;

    this.dialogRef.close({
      confirm: true,
      idTipoTributarioDestino: this.modo === 'reatribuir' ? this.idDestino : null,
    } as ResolverVinculoModalResult);
  }
}
